import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-environment-info',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Version complète (dev ou prod avec debug) -->
    <div
      class="environment-info"
      [ngClass]="getEnvironmentClass()"
      *ngIf="showInfo"
    >
      <div class="env-badge">
        <span class="env-label">Environment:</span>
        <span class="env-value">{{ environment.environment }}</span>
      </div>
      <div class="version-badge">
        <span class="version-label">Version:</span>
        <span class="version-value">{{ environment.version }}</span>
      </div>
      <div class="debug-badge" *ngIf="environment.debug">
        <span class="debug-label">Debug:</span>
        <span class="debug-value">{{ environment.debug ? 'ON' : 'OFF' }}</span>
      </div>
      <div class="api-badge" *ngIf="environment.debug">
        <span class="api-label">API:</span>
        <span class="api-value">{{ environment.apiUrl }}</span>
      </div>
    </div>

    <!-- Version minimale (toujours visible en production) -->
    <div class="version-only" *ngIf="!showInfo && environment.production">
      <div class="version-minimal">
        <span class="version-label">v{{ environment.version }}</span>
      </div>
    </div>
  `,
  styles: [
    `
      .environment-info {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 8px 16px;
        font-size: 12px;
        font-family: 'Courier New', monospace;
        z-index: 9999;
        display: flex;
        justify-content: space-between;
        align-items: center;
        backdrop-filter: blur(5px);
        border-top: 1px solid rgba(255, 255, 255, 0.1);
      }

      .env-badge,
      .version-badge,
      .debug-badge,
      .api-badge {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .env-label,
      .version-label,
      .debug-label,
      .api-label {
        opacity: 0.7;
        font-weight: normal;
      }

      .env-value,
      .version-value,
      .debug-value,
      .api-value {
        font-weight: bold;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 11px;
      }

      /* Styles par environnement */
      .environment-info.development {
        background: rgba(34, 197, 94, 0.9);
      }

      .environment-info.development .env-value {
        background: rgba(255, 255, 255, 0.2);
        color: white;
      }

      .environment-info.production {
        background: rgba(239, 68, 68, 0.9);
      }

      .environment-info.production .env-value {
        background: rgba(255, 255, 255, 0.2);
        color: white;
      }

      .version-value {
        background: rgba(255, 255, 255, 0.2);
        color: white;
      }

      .debug-value {
        background: rgba(255, 193, 7, 0.8);
        color: #000;
      }

      .api-value {
        background: rgba(59, 130, 246, 0.8);
        color: white;
        font-size: 10px;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Responsive */
      @media (max-width: 768px) {
        .environment-info {
          flex-direction: column;
          gap: 4px;
          padding: 6px 12px;
          font-size: 11px;
        }

        .env-badge,
        .version-badge,
        .debug-badge,
        .api-badge {
          justify-content: center;
        }
      }

      /* Masquer en production si debug est false */
      .environment-info.production:not(.debug-mode) {
        display: none;
      }

      /* Version minimale pour la production */
      .version-only {
        position: fixed;
        bottom: 0;
        right: 0;
        z-index: 9999;
      }

      .version-minimal {
        background: rgba(0, 0, 0, 0.7);
        color: white;
        padding: 4px 8px;
        border-radius: 4px 0 0 0;
        font-size: 11px;
        font-family: 'Courier New', monospace;
        backdrop-filter: blur(5px);
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        border-left: 1px solid rgba(255, 255, 255, 0.1);
      }

      .version-minimal .version-label {
        font-weight: bold;
        opacity: 0.9;
      }

      /* Responsive pour la version minimale */
      @media (max-width: 768px) {
        .version-minimal {
          font-size: 10px;
          padding: 3px 6px;
        }
      }
    `,
  ],
})
export class EnvironmentInfoComponent implements OnInit {
  environment = environment;
  showInfo = false;

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    // Afficher l'info si :
    // 1. On est en développement (debug = true)
    // 2. On est en production mais avec le paramètre ?debug=true
    this.showInfo =
      this.environment.debug ||
      this.route.snapshot.queryParams['debug'] === 'true' ||
      localStorage.getItem('showEnvInfo') === 'true';

    // Log de l'environnement dans la console pour debug
    if (this.environment.debug) {
      console.log('🌍 Environment Info:', {
        environment: this.environment.environment,
        version: this.environment.version,
        production: this.environment.production,
        apiUrl: this.environment.apiUrl,
        debug: this.environment.debug,
        logLevel: this.environment.logLevel,
      });
    }
  }

  getEnvironmentClass(): string {
    let classes = this.environment.environment;
    if (this.environment.debug) {
      classes += ' debug-mode';
    }
    return classes;
  }
}
